import {
    Card,
    Button,
    ButtonGroup,
} from 'reactstrap';
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TextInput, DollarInput } from '~/shared/components/form/Inputs';
import UploadDocument from './UploadDocuments';
import { DocumentInfoButton } from './DocumentInfoModal';
import { FundRequestContext } from '~/context/fundrequests';

const MedicalExpenses = ({ fieldArray, handleSubmission, categoryValue }) => {
    const {
        control,
        watch,
        setValue,
        getValues,
        register,
        clearErrors,
        formState: { errors, isSubmitting, isDirty, isValid },
    } = useFormContext();
    const [{ expenseItem, expenseIndex, formKey, uploadPath }, fundRequestDispatch] =
        useContext(FundRequestContext);
    const { remove } = fieldArray;
    const hasValidId = typeof expenseItem?.id === 'number';
    const showUploads = hasValidId || !!uploadPath;

    // Early return if formKey is not available yet
    if (!formKey) {
        return <div>Loading form...</div>;
    }

    // Clear errors when formKey changes (new expense item)
    useEffect(() => {
        if (formKey) {
            clearErrors(`${formKey}.expense_date`);
            clearErrors(`${formKey}.end_date`);
            clearErrors(`${formKey}.amount`);
            clearErrors(`${formKey}.description`);

            // Also ensure amount is set to 0 initially
            if (getValues(`${formKey}.amount`) === undefined) {
                setValue(`${formKey}.amount`, 0);
            }
        }
    }, [formKey, clearErrors, setValue, getValues]);

    const handleDeleteItem = () => {
        remove(expenseIndex);
        fundRequestDispatch({
            type: 'reset_expense_item',
        });
    };

    const handleCancelItem = () => {
        if (!expenseItem?.id) {
            handleDeleteItem();
        } else {
            console.log(
                'expenseItem has id so just resetting at cancel: ',
                expenseItem
            );
            fundRequestDispatch({
                type: 'reset_expense_item',
            });
        }
    };

    // For some reason, isDirty doesn't change when the total changes
    // Capture changes separately
    const total = watch(`${formKey}.amount`);
    const existingUploads = watch(`${formKey}.existing_uploads`);
    const descriptionValue = watch(`${formKey}.description`) || '';
    const DESCRIPTION_MAX = 60;
    // Check if total is explicitly defined and greater than 0
    const totalIsValid = total !== undefined && total !== null && total > 0;
    const existingTotal = expenseItem.amount;
    const totalChanged = total !== existingTotal;

    const [saving, setSaving] = useState(false);
    const hasErrors = Object.keys(errors).length > 0;
    const saveState = isDirty || totalChanged || existingUploads?.length;
    const saveText = saving || isSubmitting ? 'Saving...' : !saveState ? 'Done' : 'Save';

    return (
        <Card style={{ background: 'inherit' }}>
            <div className='d-sm-flex gap-3 mt-3'>
                <div>
                    <TextInput
                        name={`${formKey}.expense_date`}
                        label='Expense Date'
                        type='date'
                        control={control}
                        required={true}
                        aria-required={true}
                    />
                </div>
                <div>
                    <TextInput
                        name={`${formKey}.end_date`}
                        label='End Date (optional)'
                        type='date'
                        control={control}
                        required={false}
                    />
                </div>
                <div>
                    <DollarInput
                        name={`${formKey}.amount`}
                        label='Total'
                        control={control}
                        required={true}
                        ariaRequired={true}
                        defaultValue={getValues(`${formKey}.amount`) || ''}
                        setValue={setValue}
                    />
                </div>
            </div>
            <div className='flex-1 mb-2'>
                <TextInput
                    label='Description'
                    name={`${formKey}.description`}
                    control={control}
                    required={true}
                    aria-required={true}
                    maxLength={DESCRIPTION_MAX}
                    newRules={{
                        maxLength: {
                            value: DESCRIPTION_MAX,
                            message: 'Description must be 60 characters or less'
                        },
                        pattern: {
                            value: /^[^"]*$/,
                            message: 'Description cannot contain double quote characters (")'
                        }
                    }}
                />
                <div
                    style={{
                        textAlign: 'right',
                        marginTop: '-6px',
                        fontSize: '12px',
                        color: '#6c757d',
                    }}
                >
                    {descriptionValue.length}/{DESCRIPTION_MAX}
                </div>
            </div>
            {showUploads && <UploadDocument />}
            <DocumentInfoButton categoryValue={categoryValue} />
            <ButtonGroup className='d-grid d-sm-flex gap-2 mt-4'>
                <Button
                    color='outline-dark'
                    onClick={handleCancelItem}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                {hasValidId && (
                    <>
                        <Button
                            color='danger'
                            onClick={handleDeleteItem}
                            disabled={isSubmitting}
                        >
                            Delete
                        </Button>
                        <Button
                            color='primary'
                            onClick={async () => {
                                // If nothing to save, close the form (Done)
                                if (!saveState) {
                                    fundRequestDispatch({ type: 'reset_expense_item' });
                                    return;
                                }
                                try {
                                    setSaving(true);
                                    await handleSubmission({ permanentSave: false });
                                } finally {
                                    setSaving(false);
                                }
                            }}
                            disabled={
                                hasErrors ||
                                isSubmitting || saving ||
                                !isValid ||
                                !totalIsValid
                            }
                        >
                            {saveText}
                        </Button>
                    </>
                )}
                {!hasValidId && (
                    <Button
                        color='primary'
                        onClick={async () => {
                            // If nothing to save, close the form (Done)
                            if (!saveState) {
                                fundRequestDispatch({ type: 'reset_expense_item' });
                                return;
                            }
                            try {
                                setSaving(true);
                                const updatedItem = await handleSubmission({ draft: true, permanentSave: false });
                                if (updatedItem && typeof updatedItem.id === 'number') {
                                    fundRequestDispatch({
                                        type: 'set_expense_item',
                                        expenseItem: updatedItem,
                                        expenseType: 'default',
                                    });
                                }
                            } finally {
                                setSaving(false);
                            }
                        }}
                        disabled={
                            hasErrors ||
                            isSubmitting || saving ||
                            !isValid ||
                            !totalIsValid
                        }
                    >
                        {saving || isSubmitting ? `Saving...` : `Next: Upload Documents`}
                    </Button>
                )}
            </ButtonGroup>
        </Card>
    );
};

export default MedicalExpenses;
